// Client-side prediction for the local player.
//
// The client runs the same plane physics (shared module) every frame using the
// most recent input. Position/orientation update IMMEDIATELY — no waiting for
// the server round-trip. When an authoritative snapshot arrives, we smoothly
// nudge the predicted state toward the server value (positional + orientation
// blend with low gain) so any drift gets absorbed without visible teleport.
// On respawn or large divergence we hard-snap.

import * as THREE from "three";
import { stepPlane, type PlaneState, type PlaneInput, qFromAxisAngle } from "../shared/Physics";
import type { PlayerSnapshot } from "../shared/protocol";
import { SKILLS } from "../shared/protocol";

const SOFT_POS_LERP = 0.06;   // per-frame blend toward server position when divergence is small
const SOFT_QUAT_LERP = 0.08;  // per-frame slerp toward server orientation
const HARD_SNAP_DIST = 80;    // beyond this, snap immediately
const HARD_SNAP_VEL  = 220;   // beyond this velocity divergence too

export class LocalPredictor {
  state: PlaneState = {
    px: 0, py: 200, pz: 0,
    vx: 0, vy: 0, vz: 0,
    qx: 0, qy: 0, qz: 0, qw: 1,
    wpitch: 0, wyaw: 0, wroll: 0,
    engineRpm: 0.7,
  };
  // local-only skill state (server is still authoritative; we mirror for prediction)
  dashT = 0;
  alive = false;
  initialized = false;
  // Last server-authoritative state, kept around for soft correction
  private lastServer: PlaneState | null = null;
  private lastServerStamp = 0;

  // Apply input + advance one tick locally.
  step(input: PlaneInput, dt: number, dashJustTriggered: boolean) {
    if (!this.alive || !this.initialized) return;
    if (dashJustTriggered) this.dashT = SKILLS.dash.duration;
    if (this.dashT > 0) this.dashT = Math.max(0, this.dashT - dt);
    const dashBoost = this.dashT > 0 ? SKILLS.dash.boost : 0;
    const r = stepPlane(this.state, input, dt, dashBoost, dashBoost);
    if (r.crashed) {
      // Server will send an authoritative snapshot; mark not alive locally.
      this.alive = false;
    }
  }

  // Called when an authoritative snapshot of the local player arrives.
  applyServerSnapshot(p: PlayerSnapshot, justRespawned: boolean) {
    const sv: PlaneState = {
      px: p.x, py: p.y, pz: p.z,
      vx: p.vx, vy: p.vy, vz: p.vz,
      qx: p.qx, qy: p.qy, qz: p.qz, qw: p.qw,
      wpitch: this.state.wpitch, wyaw: this.state.wyaw, wroll: this.state.wroll,
      engineRpm: p.engineRpm,
    };
    this.lastServer = sv;
    this.lastServerStamp = performance.now();

    if (!this.initialized || justRespawned) {
      // Hard sync
      this.state.px = sv.px; this.state.py = sv.py; this.state.pz = sv.pz;
      this.state.vx = sv.vx; this.state.vy = sv.vy; this.state.vz = sv.vz;
      this.state.qx = sv.qx; this.state.qy = sv.qy; this.state.qz = sv.qz; this.state.qw = sv.qw;
      this.state.wpitch = 0; this.state.wyaw = 0; this.state.wroll = 0;
      this.state.engineRpm = sv.engineRpm;
      this.dashT = 0;
      this.initialized = true;
      this.alive = p.alive;
      return;
    }

    this.alive = p.alive;
    if (!p.alive) return;

    const dx = sv.px - this.state.px;
    const dy = sv.py - this.state.py;
    const dz = sv.pz - this.state.pz;
    const distSq = dx * dx + dy * dy + dz * dz;
    const velDiv = Math.hypot(sv.vx - this.state.vx, sv.vy - this.state.vy, sv.vz - this.state.vz);

    if (distSq > HARD_SNAP_DIST * HARD_SNAP_DIST || velDiv > HARD_SNAP_VEL) {
      // Diverged too much — snap.
      this.state.px = sv.px; this.state.py = sv.py; this.state.pz = sv.pz;
      this.state.vx = sv.vx; this.state.vy = sv.vy; this.state.vz = sv.vz;
      this.state.qx = sv.qx; this.state.qy = sv.qy; this.state.qz = sv.qz; this.state.qw = sv.qw;
    }
    // Soft drift correction is applied in correctTowardServer() each frame.
  }

  // Called every frame after step() to gently pull predicted toward server.
  correctTowardServer() {
    const sv = this.lastServer;
    if (!sv || !this.alive) return;
    // Position drift
    this.state.px += (sv.px - this.state.px) * SOFT_POS_LERP;
    this.state.py += (sv.py - this.state.py) * SOFT_POS_LERP;
    this.state.pz += (sv.pz - this.state.pz) * SOFT_POS_LERP;
    // Velocity drift
    this.state.vx += (sv.vx - this.state.vx) * SOFT_POS_LERP;
    this.state.vy += (sv.vy - this.state.vy) * SOFT_POS_LERP;
    this.state.vz += (sv.vz - this.state.vz) * SOFT_POS_LERP;
    // Engine drift (subtle — server is authoritative, the client predictor should land
    // on the same value on its own thanks to identical physics, but a small nudge is harmless).
    this.state.engineRpm += (sv.engineRpm - this.state.engineRpm) * SOFT_POS_LERP * 0.5;
    // Orientation drift via slerp
    const a = new THREE.Quaternion(this.state.qx, this.state.qy, this.state.qz, this.state.qw);
    const b = new THREE.Quaternion(sv.qx, sv.qy, sv.qz, sv.qw);
    a.slerp(b, SOFT_QUAT_LERP);
    this.state.qx = a.x; this.state.qy = a.y; this.state.qz = a.z; this.state.qw = a.w;
  }

  pos(): THREE.Vector3 { return new THREE.Vector3(this.state.px, this.state.py, this.state.pz); }
  quat(): THREE.Quaternion { return new THREE.Quaternion(this.state.qx, this.state.qy, this.state.qz, this.state.qw); }
}
